import React, { useCallback, useState } from 'react';

import { SearchOutlined, EyeOutlined } from '@ant-design/icons';
import { ButtonAction } from '@fastlessjs/backoffice-layer/dist/components/atoms';
import { PageProps } from '@fastlessjs/backoffice-layer/dist/components/pages/types';
import { useMultiTenancy } from '@fastlessjs/backoffice-layer/dist/hooks';
import debug from '@fastlessjs/backoffice-layer/dist/utils/debug';
import { Input, Modal, Typography, Space, Button } from 'antd';
import { APIOptions } from 'types';

import { t } from '../../config/translations';
import { useOrder } from '../hooks';

// Register this component for debug tracking
debug.registerComponent('SearchOrderModal');

const Table = ({ org }: PageProps) => {
  debug.trackRender('SearchOrderModal');
  debug.log('SearchOrderModal', `Rendering with org: ${org}`);
  const { getPage } = useMultiTenancy();
  const enterpriseId = getPage('enterprises')?.[org]?.enterpriseId;
  const { Text } = Typography;

  const [orderId, setOrderId] = useState<string | undefined>(undefined);
  const [inputValue, setInputValue] = useState<string>('');
  const [options, setOptions] = useState<APIOptions>({ orderId: undefined });

  const setOrderOptions = useCallback((id?: string) => {
    if (id) {
      const optionsToSet = {
        orderId: id,
        clientId: enterpriseId,
      };
      return setOptions(optionsToSet);
    }
    return setOptions({
      orderId: undefined,
    });
  }, []);

  const { data, isLoading } = useOrder(options);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setInputValue('');
    setOrderOptions();
    setIsModalOpen(false);
  }, []);

  const handleSearchOrder = useCallback((id?: string) => {
    if (id && id !== '' && id.length > 5) {
      debug.log('SearchOrderModal', `Searching for order: ${id}`);
      setOrderOptions(id);
    }
  }, []);

  return (
    <>
      <ButtonAction type="primary" onClick={handleOpenModal}>
        {t('searchModal.searchOrder')}
      </ButtonAction>
      <Modal
        title={t('searchModal.searchAnOrder')}
        open={isModalOpen}
        onCancel={handleCloseModal}
        bodyStyle={{ padding: '12px 24px' }}
        footer={[
          <Button
            key="submit"
            type={data ? 'default' : 'primary'}
            icon={data ? <EyeOutlined /> : <SearchOutlined />}
            onClick={() => handleSearchOrder(orderId)}
            loading={isLoading}
            style={{
              backgroundColor: data ? 'var(--color-success)' : undefined,
              borderColor: data ? 'var(--color-success)' : undefined,
              color: data ? 'white' : undefined,
            }}
            data-navigate-path={data && `/${org}/orders/${data.ndoc}/view`}
          >
            {data ? t('searchModal.openDetails') : t('searchModal.search')}
          </Button>,
        ]}
      >
        <Space direction="vertical" size="middle" style={{ display: 'flex' }}>
          <Text>{t('searchModal.enterOrderNumber')}</Text>
          <Input
            placeholder={t('searchModal.orderPlaceholder')}
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              setOrderId(e.target.value);
            }}
          />
        </Space>
      </Modal>
    </>
  );
};

export default Table;

// Mark this component as initialized
debug.markInitialized('SearchOrderModal');
